import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { BetaReaderShell } from '../components/BetaReaderShell';
import { HomeSwitch } from '../components/HomeSwitch';
import { CopyLinkControl } from '../components/CopyLinkControl';
import { SearchWindowToggle } from '../components/SearchWindowToggle';
import { IconLink, IconRss, IconTrash, IconSearch } from '../components/icons';
import { useUser } from '../providers/UserProvider';
import { listBetaSearches, saveBetaSearch, setSearchIncluded, deleteBetaSearch, restoreBetaSearch, type BetaSearch } from '../repositories/betaSearchRepository';
import { getAnonymousInterestsCookie, reconcileAnonymousInterests, setAnonymousInterestsCookie } from '../utils/anonymousInterestsCookie';
import { readerEvent } from '../utils/readerTelemetry';
import { extractErrorMessage } from '../api';
import { useReaderRoutes } from '../providers/ReaderRoutesProvider';
import { storedStoryCounts } from '../repositories/feedsV2Repository';
import { readerFeedList } from '../utils/feedSwitcher';

export function setFeedIncluded(items: BetaSearch[], id: string, included: boolean): BetaSearch[] {
  return items.map(item => item.id === id ? { ...item, included } : item);
}

/**
 * The reader's feeds, with their Home switches, links and exports. Each search
 * shows its stories today from its last stored v2 build (a feed that has not
 * built yet shows none).
 */
export default function ReaderFeedsPage() {
  const { isLoggedIn } = useUser();
  const routes = useReaderRoutes();
  const [feeds, setFeeds] = useState<BetaSearch[]>([]);
  const [owned, setOwned] = useState<Set<string>>(new Set());
  const [deleted, setDeleted] = useState<Set<string>>(new Set());
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [busy, setBusy] = useState(false);
  const [toggling, setToggling] = useState<Set<string>>(new Set());
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [hours, setHours] = useState<24 | 72>(() => localStorage.getItem('reader_home_hours') === '72' ? 72 : 24);
  useEffect(() => {
    let disposed = false;
    void listBetaSearches().then(data => {
      if (disposed) return;
      const searches = isLoggedIn ? data.searches : reconcileAnonymousInterests(data.searches, data.public_searches, getAnonymousInterestsCookie());
      setOwned(new Set(searches.map(feed => feed.id)));
      setFeeds(readerFeedList(searches, data.public_searches));
      setLoading(false);
      const own = searches.filter(feed => !feed.is_public).map(feed => feed.id).slice(0, 100);
      if (own.length) void storedStoryCounts(own, 24).then(stored => { if (!disposed) setCounts(stored); }).catch(() => {});
    }).catch(error => { if (!disposed) { setError(extractErrorMessage(error)); setLoading(false); } });
    return () => { disposed = true; };
  }, [isLoggedIn]);
  // Optimistic: flip the switch now, revert with a short error if the write fails.
  async function toggle(feed: BetaSearch, on: boolean) {
    setError(''); setToggling(value => new Set(value).add(feed.id));
    setFeeds(items => setFeedIncluded(items, feed.id, on));
    try {
      if (isLoggedIn) {
        if (owned.has(feed.id)) await setSearchIncluded(feed.id, on);
        else await saveBetaSearch(feed.description, feed.id, on);
      } else setAnonymousInterestsCookie(setFeedIncluded(feeds, feed.id, on));
      setOwned(value => new Set(value).add(feed.id));
      readerEvent('feed_home_toggle', { feed_id: feed.id, on, method: 'feeds_switch' });
    } catch (error) {
      setFeeds(items => setFeedIncluded(items, feed.id, !on));
      setError(`Couldn’t update “${feed.name}”: ${extractErrorMessage(error)}`);
    } finally { setToggling(value => { const next = new Set(value); next.delete(feed.id); return next; }); }
  }
  async function remove(feed: BetaSearch, undo: boolean) {
    setBusy(true); setError('');
    try {
      if (undo) await restoreBetaSearch(feed.id, feed.included); else await deleteBetaSearch(feed.id);
      setDeleted(value => { const next = new Set(value); if (undo) next.delete(feed.id); else next.add(feed.id); return next; });
      readerEvent(undo ? 'feed_restore' : 'feed_delete', { feed_id: feed.id });
    } catch (error) { setError(extractErrorMessage(error)); }
    finally { setBusy(false); }
  }
  const count = feeds.filter(feed => feed.included && !deleted.has(feed.id)).length;
  const row = (feed: BetaSearch) => deleted.has(feed.id) ? <div className="reader-feed-row reader-deleted-feed" key={feed.id}>Deleted “{feed.name}”<button type="button" disabled={busy} onClick={() => void remove(feed, true)}>Undo</button></div> :
    <div className="reader-feed-row" key={feed.id}>
      <HomeSwitch on={feed.included} disabled={busy || toggling.has(feed.id)} label={`Show ${feed.name} in Home`} onChange={on => void toggle(feed, on)} />
      {feed.is_public
        ? <span className="reader-feed-kind" aria-hidden="true">●</span>
        : <Link className="reader-feed-kind" to={routes.feed(feed.id, { expanded: true })} aria-label={`Show search prompt for ${feed.name}`} title="Show search prompt"><IconSearch size={14} /></Link>}
      <Link className="reader-feed-name" to={routes.feed(feed.id)} title={feed.name}>{feed.name}</Link>
      {!feed.is_public && feed.id in counts && <span className="reader-feed-count">{counts[feed.id] ?? 0} today</span>}
      <span className="reader-feed-links">
        <CopyLinkControl getUrl={() => new URL(routes.feed(feed.id), window.location.origin).href} title={`Copy link to ${feed.name}`} ariaLabel={`Copy link to ${feed.name}`} copiedMessage="Link copied" errorMessage="Could not copy link"><IconLink /></CopyLinkControl>
        <a href={routes.feedExport(feed.id, 'xml', hours)} aria-label={`RSS for ${feed.name}`} title="RSS"><IconRss size={13} /></a>
        <a className="reader-mono" href={routes.feedExport(feed.id, 'md', hours)} aria-label={`Markdown for ${feed.name}`}>.md</a>
      </span>
      {!feed.is_public && <button type="button" className="reader-feed-delete" aria-label={`Delete ${feed.name}`} title={`Delete ${feed.name}`} disabled={busy || toggling.has(feed.id)} onClick={() => void remove(feed, false)}><IconTrash size={16} /></button>}
      <details className="reader-feed-menu"><summary aria-label={`Links for ${feed.name}`}>⋯</summary><div className="reader-popover">
        <CopyLinkControl getUrl={() => new URL(routes.feed(feed.id), window.location.origin).href} title="Copy link" ariaLabel="Copy link" copiedMessage="Link copied" errorMessage="Could not copy link">Copy link</CopyLinkControl>
        <a href={routes.feedExport(feed.id, 'xml', hours)}>RSS</a><a className="reader-mono" href={routes.feedExport(feed.id, 'md', hours)}>.md</a>
      </div></details>
    </div>;
  return <BetaReaderShell search={<Link className="beta-search-bar reader-feeds-search" to={routes.newSearch}><IconSearch size={16} /><span>e.g. AI model releases</span></Link>}>
    <div className="reader-feeds-page">
      <div className="reader-feeds-heading"><Link to={routes.home}>← <span>Home</span></Link><h1>Feeds</h1><span className="reader-mono">{count} in Home</span>
        <div className="reader-feeds-window"><SearchWindowToggle label="Feed time range" options={[24, 72]} selected={hours} onSelect={value => { setHours(value); localStorage.setItem('reader_home_hours', String(value)); }} /></div>
        <p>Switch a feed on to show its stories in Home.</p></div>
      {error && <p role="alert" className="beta-error">{error}</p>}{loading && <p role="status">Loading feeds…</p>}
      <section className="reader-feeds-section"><h2>Your searches</h2><p>Run a search and choose Save feed to add one.</p>{feeds.filter(feed => !feed.is_public).map(row)}</section>
      <section className="reader-feeds-section"><h2>TLDR newsletters</h2><p>Each newsletter is a feed.</p>{feeds.filter(feed => feed.is_public).map(row)}</section>
    </div>
  </BetaReaderShell>;
}
